import styled from "styled-components";
import Icon, { IconType } from "../Icon/Icon";
import { DropdownStyles } from "../../types/styles";

const Container = styled.div<{
  styles: DropdownStyles;
}>`
  display: flex;
  gap: 5px;
  flex-direction: column;
  width: 100%;
  font-size: 13px;
  color: ${({ styles }) => styles.OverallGeneralTextColour};
`;

const TextAreaAndIconContainer = styled.div<{
  styles: DropdownStyles;
  isErrored?: boolean;
}>`
  display: flex;
  align-items: center;
  border: 1px solid
    ${({ styles, isErrored }) =>
      isErrored
        ? styles.DropdownGeneralValidationErrorColour
        : styles.DropdownBorderColour};
  border-radius: ${({ styles }) => `${styles.DropdownBorderRadius}px`};
  background-color: ${({ styles }) => styles.DropdownGeneralBackgroundColour};
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
  padding: 4px 8px 4px 8px;
  box-sizing: border-box;

  &:focus-within {
    outline: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
    border: ${({ styles }) => `1px solid ${styles.DropdownBorderFocusColour}`};
  }
`;

const StyledTextArea = styled.textarea<{
  styles: DropdownStyles;
}>`
  font-family: inherit;
  border: none;
  width: 100%;
  background: none;
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
  min-height: 60px;
  line-height: 20px;
  resize: none;

  &:focus {
    border: none;
    outline: none;
  }

  &::placeholder {
    color: ${({ styles }) => styles.DropdownGeneralPlaceholderColour};
  }

  // Remove built in calendar icon because we use FontAwesome icons instead.
  &::-webkit-calendar-picker-indicator {
    display: none;
  }
`;

const StyledIcon = styled(Icon)`
  margin-right: 5px;
`;

const StyledLabel = styled.label`
  font-weight: 700;
`;

const StyledSpan = styled.span`
  font-weight: normal;
  margin-left: 4px;
`;

const ErrorMessage = styled.div<{
  styles: DropdownStyles;
}>`
  color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
`;

export type TextAreaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange"
> & {
  onChange: (value: string | number) => void;
  label?: string;
  leftIcon?: IconType;
  bold?: boolean;
  optional?: boolean;
  showCounter?: boolean;
  styles: DropdownStyles;
  errorMessage?: string;
  dataId?: string;
};

export const TextArea: React.FC<TextAreaProps> = ({
  onChange,
  label,
  leftIcon,
  optional,
  showCounter,
  styles,
  errorMessage,
  dataId,
  ...props
}) => {
  return (
    <Container styles={styles} data-id={dataId + "-container"}>
      {label && (
        <StyledLabel htmlFor={label}>
          {label}
          {optional && <StyledSpan>(Optional)</StyledSpan>}
        </StyledLabel>
      )}
      <TextAreaAndIconContainer isErrored={!!errorMessage} styles={styles}>
        {leftIcon && <StyledIcon icon={leftIcon} />}
        <StyledTextArea
          {...props}
          id={label}
          onChange={(event) => onChange(event.target.value)}
          styles={styles}
          data-id={dataId + "-textarea"}
        />
      </TextAreaAndIconContainer>
      {showCounter && (
        <StyledSpan>
          {props.value ? props.value.toString().length : "0"} /{" "}
          {props.maxLength}
        </StyledSpan>
      )}
      {errorMessage && (
        <ErrorMessage styles={styles}>{errorMessage}</ErrorMessage>
      )}
    </Container>
  );
};

export default TextArea;
